/**
 * Chapter002.js — Project Details & Introduction (OHSE 002)
 *
 * Props:
 *   projectInfo  {object}  derived project info
 *   companyInfo  {object}  from GET /api/settings
 *   swmsData     {object}  full swms document data
 *   isAdmin      {boolean} user has company_admin / system_admin role
 *   onChange     {fn}      (field, value) => void — for SWMS fields (fax etc.)
 */

import { useState, useEffect, useCallback } from 'react';
import { Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { refreshCompanyBrand } from '../../../lib/brandConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

const COMPANY_FIELDS = [
  { key: 'name', label: 'Company Name' },
  { key: 'acn', label: 'ABN/ACN' },
  { key: 'address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

export default function Chapter002({
  projectInfo = {},
  companyInfo = {},
  swmsData = {},
  isAdmin = false,
  onChange,
}) {
  const [companyFields, setCompanyFields] = useState({});
  const [savingCompany, setSavingCompany] = useState(null);
  const [savedCompany, setSavedCompany] = useState(null);

  // Sync local state from companyInfo when it changes
  useEffect(() => {
    setCompanyFields({
      name: companyInfo?.name || '',
      acn: companyInfo?.acn || '',
      address: companyInfo?.address || '',
      phone: companyInfo?.phone || '',
      email: companyInfo?.email || '',
    });
  }, [
    companyInfo?.name,
    companyInfo?.acn,
    companyInfo?.address,
    companyInfo?.phone,
    companyInfo?.email,
  ]);

  // Save a company field → PUT /api/settings/company-brand
  const saveCompanyField = useCallback(async (field, value) => {
    if (!isAdmin) return;
    setSavingCompany(field);
    try {
      await axios.put(`${API}/settings/company-brand`, { [field]: value });
      setSavedCompany(field);
      setTimeout(() => setSavedCompany(null), 2000);
      await refreshCompanyBrand();
      window.dispatchEvent(new CustomEvent('company-brand-updated'));
    } catch (err) {
      console.error('Company field save error:', err);
    }
    setSavingCompany(null);
  }, [isAdmin]);

  const handleCompanyBlur = useCallback(
    (field, value) => {
      if (!isAdmin) return;
      saveCompanyField(field, value);
    },
    [isAdmin, saveCompanyField],
  );

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">
          OHSE 002
        </p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Project Details &amp; Introduction
        </h2>
      </div>

      {/* ── Company Information ── */}
      <Section title="Company Information">
        <div
          className={
            isAdmin
              ? 'grid grid-cols-2 gap-x-6 gap-y-3 text-sm'
              : 'grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm'
          }
        >
          {COMPANY_FIELDS.map(({ key, label }) =>
            isAdmin ? (
              <div key={key}>
                <label className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {label}
                  </span>
                  {savingCompany === key && (
                    <Loader2 className="w-3 h-3 animate-spin text-[#c9a84c]" />
                  )}
                  {savedCompany === key && (
                    <Check className="w-3 h-3 text-[#10b981]" />
                  )}
                </label>
                <input
                  type="text"
                  value={companyFields[key] || ''}
                  onChange={(e) =>
                    setCompanyFields((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  onBlur={(e) => handleCompanyBlur(key, e.target.value)}
                  className={inputCls}
                  placeholder={label}
                />
              </div>
            ) : (
              <Kv key={key} label={label} value={companyFields[key]} />
            ),
          )}
          {/* Fax — project-specific SWMS field, editable via onChange */}
          {onChange ? (
            <div>
              <label className="flex items-center gap-1.5 mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Fax
                </span>
              </label>
              <input
                type="text"
                value={swmsData.company_fax || ''}
                onChange={(e) => onChange('company_fax', e.target.value)}
                className={inputCls}
                placeholder="Fax"
              />
            </div>
          ) : (
            <Kv label="Fax" value={swmsData.company_fax} />
          )}
        </div>
        {isAdmin && (
          <p className="text-[10px] text-gray-400 mt-2">
            Changes to company fields above are saved directly to Company Profile.
          </p>
        )}
      </Section>

      {/* ── Project Information ── */}
      <Section title="Project Information">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <Kv label="Project Name" value={projectInfo.project_name} />
          <Kv label="Site Address" value={projectInfo.project_address} />
          <Kv label="Client Name" value={projectInfo.client_name} />
          <Kv label="Principal Contractor" value={swmsData.principal_contractor} />
          <Kv label="Project Type" value={projectInfo.project_type} />
          <Kv label="State" value={projectInfo.state} />
          <Kv label="Start Date" value={projectInfo.project_start_date} />
          <Kv label="End Date" value={projectInfo.project_end_date} />
          <Kv label="Site Manager" value={swmsData.site_manager_name} />
          <Kv label="Manager Phone" value={swmsData.site_manager_phone} />
          <Kv label="Manager Email" value={swmsData.site_manager_email} />
        </div>
      </Section>

      {/* ── Scope of Works ── */}
      <Section title="Scope of Works">
        <p className="text-sm text-[#1a1a2e] leading-relaxed whitespace-pre-wrap">
          {swmsData.scope_of_works || 'No scope of works entered.'}
        </p>
      </Section>
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg p-4">
      <h3 className="text-sm font-semibold text-[#1a1a2e] mb-3 uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 min-w-[130px] shrink-0">{label}:</span>
      <span className="text-[#1a1a2e]">{value || '—'}</span>
    </div>
  );
}
